import React, { useState, useEffect } from 'react';
import api from '../../config/api';
import { useToast } from '../../contexts/ToastContext';

interface ShippingContactModalProps {
  show: boolean;
  onClose: () => void;
  onSuccess: () => void;
  draftId: string;
  draftNumber?: string;
  initialContactName?: string;
  initialContactEmail?: string;
  initialContactPhone?: string;
}

const ShippingContactModal: React.FC<ShippingContactModalProps> = ({
  show,
  onClose,
  onSuccess,
  draftId,
  draftNumber,
  initialContactName = '',
  initialContactEmail = '',
  initialContactPhone = ''
}) => {
  const { showSuccess, showError } = useToast();
  const [contactName, setContactName] = useState(initialContactName);
  const [contactEmail, setContactEmail] = useState(initialContactEmail);
  const [contactPhone, setContactPhone] = useState(initialContactPhone);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; email?: string; phone?: string }>({});

  // Reset form when modal opens with initial values
  useEffect(() => {
    if (show) {
      setContactName(initialContactName);
      setContactEmail(initialContactEmail);
      setContactPhone(initialContactPhone);
      setErrors({});
    }
  }, [show, initialContactName, initialContactEmail, initialContactPhone]);

  const validateForm = (): boolean => {
    const newErrors: { name?: string; email?: string; phone?: string } = {};

    if (!contactName.trim()) {
      newErrors.name = 'Contact name is required';
    }

    if (!contactEmail.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!contactPhone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (contactPhone.replace(/\D/g, '').length < 10) {
      newErrors.phone = 'Please enter a valid phone number (at least 10 digits)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      await api.put(`/drafts/${draftId}/shipping-contact`, {
        contact_name: contactName.trim(),
        contact_email: contactEmail.trim(),
        contact_phone: contactPhone.trim()
      });

      showSuccess('Shipping contact saved successfully');
      onSuccess();
    } catch (err: any) {
      console.error('Failed to save shipping contact:', err);
      showError(err.response?.data?.detail || 'Failed to save shipping contact');
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', fontSize: '16px' }}>
      <div className="modal-dialog modal-dialog-centered" style={{
        maxWidth: '500px',
        width: '90%',
        margin: '0 auto'
      }}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="fas fa-truck me-2"></i>Shipping Contact
              {draftNumber && (
                <small className="text-muted ms-2">({draftNumber})</small>
              )}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              disabled={saving}
            ></button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="alert alert-info mb-3" style={{ textAlign: 'left' }}>
                <i className="fas fa-info-circle me-2"></i>
                Please provide contact information for the person who will receive this shipment.
                This information will be shared with anyone who views this draft.
              </div>

              <div className="mb-3">
                <label className="form-label">Contact Name *</label>
                <input
                  type="text"
                  className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                  value={contactName}
                  onChange={(e) => {
                    setContactName(e.target.value);
                    if (errors.name) setErrors({ ...errors, name: undefined });
                  }}
                  placeholder="John Doe"
                  disabled={saving}
                  autoFocus
                />
                {errors.name && <div className="invalid-feedback">{errors.name}</div>}
              </div>

              <div className="mb-3">
                <label className="form-label">Contact Email *</label>
                <input
                  type="email"
                  className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                  value={contactEmail}
                  onChange={(e) => {
                    setContactEmail(e.target.value);
                    if (errors.email) setErrors({ ...errors, email: undefined });
                  }}
                  placeholder="john.doe@hotel.com"
                  disabled={saving}
                />
                {errors.email && <div className="invalid-feedback">{errors.email}</div>}
              </div>

              <div className="mb-3">
                <label className="form-label">Contact Phone *</label>
                <input
                  type="tel"
                  className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                  value={contactPhone}
                  onChange={(e) => {
                    setContactPhone(e.target.value);
                    if (errors.phone) setErrors({ ...errors, phone: undefined });
                  }}
                  placeholder="(555) 123-4567"
                  disabled={saving}
                />
                {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save me-2"></i>
                    Save & Continue
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ShippingContactModal;
